import { getCurrentScope, onScopeDispose, onUnmounted } from 'vue'
import type { AxiosResponse, AxiosError } from 'axios'
import { type PiniaCustomProperties } from 'pinia'

import type {
  EndpointConfig,
  AllowedRequestConfigProperties,
  ApiHydraCollectionResponse,
  ApiErrorStandardRFC,
  WatchRequestConfig,
  CustomAxiosRequestConfig,
} from '@services/api/declarations'

import { axiosInstance } from '@services/api/apiClient'

import { devLogger } from '@libs/fsc/utils/logger'
import { apiURL } from '@core/config'

import { StoreSendRequestError } from '../errorHandling'

// ***
// DEFAULTS
// ***
const defaultCooldownTime = 0
const lastRequestTimes = new Map<string, number>()

// ***
// TYPES
// ***
export type RequestOptions = {
  cooldownTime?: number
  saveToStore?: boolean
}

// ***
// MAIN FUNCTION
// ***

export function createRateLimitedRequest<
  RequestConfig,
>({
  sendRequest,
  endpoint,
  requestConfig,
  serviceName,
  saveToStore = false,
  endpointName = 'main',
  cooldownTime = defaultCooldownTime,
}: {
  sendRequest: PiniaCustomProperties['sendRequest'];
  endpoint: EndpointConfig;
  requestConfig?: AllowedRequestConfigProperties<RequestConfig>;
  serviceName: string;
  cooldownTime?: number;
  enableWatch?: boolean;
  endpointName?: keyof typeof apiURL;
  saveToStore?: boolean;
}) {
  // Rate-limiting logic
  const now = Date.now()
  const lastTime = lastRequestTimes.get(endpoint.storeKey) || 0

  if (now - lastTime < cooldownTime) {
    // eslint-disable-next-line no-console
    devLogger(`Request blocked due to cooldown time: ${endpoint.storeKey} ${endpoint.url}`, 'warn')
    return
  }

  lastRequestTimes.set(endpoint.storeKey, now)

  try {
    sendRequest({
      endpointConfig: endpoint,
      requestConfig: requestConfig || {},
      endpointName,
      saveToStore,
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    throw new StoreSendRequestError({
      message: errorMessage,
      caller: endpoint.storeKey,
      service: serviceName,
      ...(errorMessage && { error: errorMessage }),
    })
  }
}

// ***
// API DATA PARSER
// ***

export function apiDataParser<R>(
  response: AxiosResponse<R>,
): { data: R | undefined, totalItems: number } {
  const hydra = response.data as ApiHydraCollectionResponse<R>
  if (hydra?.['hydra:member']) {
    return {
      data: hydra['hydra:member'] as R,
      totalItems: hydra['hydra:totalItems'],
    }
  }

  if (Array.isArray(response.data)) return { data: response.data, totalItems: response.data.length }

  return { data: response.data, totalItems: 0 }
}

// ***
// WATCH REQUEST
// ***

export function watchRequest<SuccessData, ErrorData = ApiErrorStandardRFC>(
  endpointConfig: EndpointConfig,
  {
    onRequest,
    onSuccess,
    onError,
  }: WatchRequestConfig<SuccessData, ErrorData> = {},
) {
  const requestInterceptor = axiosInstance.interceptors.request.use(
    (config: CustomAxiosRequestConfig) => {
      if (config.endpointKey === endpointConfig.storeKey) {
        onRequest?.()
      }
      return config
    },
    (error) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      onError?.(error)
      return Promise.reject(error)
    },
  )
  const responseInterceptor = axiosInstance.interceptors.response.use(
    (response: AxiosResponse<SuccessData>) => {
      if ((response.config as CustomAxiosRequestConfig).endpointKey === endpointConfig.storeKey) {
        const { data, totalItems } = apiDataParser<SuccessData>(response)
        onSuccess?.(data, totalItems)
      }
      return response
    },
    (error: AxiosError<ErrorData>) => {
      if (error.response) {
        const { data } = apiDataParser<ErrorData>(error.response)
        onError?.(data)
      }
      return Promise.reject(error)
    },
  )

  const closeSubscription = () => {
    axiosInstance.interceptors.request.eject(requestInterceptor)
    axiosInstance.interceptors.response.eject(responseInterceptor)
  }

  if (getCurrentScope()) {
    onScopeDispose(() => closeSubscription())
  } else {
    devLogger(`[watch${endpointConfig.storeKey}] was used outside of setup() and must be manually cleaned up.`, 'warn')
    onUnmounted(() => closeSubscription())
  }

  return { closeSubscription }
}
